// src/pages/AccountSettings.js   —  route: /account
//
// ACCOUNT SETTINGS, reached from the small "Account settings" link next to
// Logout on the dashboards. Three sections:
//
//   1. Your account     name, email, account type
//   2. Password         change it (asks for the current one first), or
//                       email yourself a reset link
//   3. Subscription     plan, next charge / end date, and — at the very
//                       bottom, in plain small text — "Cancel subscription"
//
// Cancelling is two steps (a short "are you sure / why" form, then confirm)
// and ends the plan at the end of the period already paid for, so nobody
// loses days they paid for. "Keep my subscription" undoes it until then.
// The work is done by functions/billingSelfService.js.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase/firebaseConfig";
import { dashboardPathFor } from "../utils/accountRole";
import { isSyntheticStudentEmail } from "../utils/studentLogin";
import { PLANS } from "../config/plans";

const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");

const REASONS = ["It costs too much", "We're not using it enough", "The school year is over", "Missing something we need", "Something isn't working", "Other"];

export default function AccountSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  // password
  const [pw, setPw] = useState({ current: "", next: "", again: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // subscription
  const [sub, setSub] = useState(null); // null = loading
  const [subErr, setSubErr] = useState("");
  const [cancelStep, setCancelStep] = useState(0); // 0 closed, 1 reason, 2 confirm
  const [reason, setReason] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [subMsg, setSubMsg] = useState(null);

  useEffect(() => {
    const stop = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate("/login", { replace: true });
        return;
      }
      setUser(u);
      const snap = await getDoc(doc(db, "users", u.uid)).catch(() => null);
      setProfile(snap && snap.exists() ? snap.data() : {});
      try {
        const res = await httpsCallable(functions, "getMySubscription")();
        setSub(res.data || { hasSubscription: false });
      } catch (err) {
        setSub({ hasSubscription: false });
        setSubErr("We couldn't load your subscription just now. Try again in a moment.");
      }
    });
    return () => stop();
  }, [navigate]);

  if (!user || !profile) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  const schoolLogin = isSyntheticStudentEmail(user.email);
  const name = [profile.sirName, profile.firstName, profile.lastName].filter(Boolean).join(" ") || profile.name || "";

  // ---- password -----------------------------------------------------------
  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pw.next.length < 6) return setPwMsg({ tone: "bad", text: "The new password needs at least 6 characters." });
    if (pw.next !== pw.again) return setPwMsg({ tone: "bad", text: "The two new passwords don't match." });
    setPwBusy(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, pw.current));
      await updatePassword(user, pw.next);
      setPw({ current: "", next: "", again: "" });
      setPwMsg({ tone: "ok", text: "Your password is changed." });
    } catch (err) {
      const code = err?.code || "";
      setPwMsg({
        tone: "bad",
        text:
          code.includes("wrong-password") || code.includes("invalid-credential")
            ? "Your current password isn't right."
            : code.includes("too-many-requests")
            ? "Too many tries — wait a few minutes and try again."
            : code.includes("weak-password")
            ? "That password is too easy to guess. Try a longer one."
            : err?.message || "Couldn't change your password.",
      });
    } finally {
      setPwBusy(false);
    }
  };

  const sendReset = async () => {
    setPwMsg(null);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setPwMsg({ tone: "ok", text: `We emailed a reset link to ${user.email}.` });
    } catch (err) {
      setPwMsg({ tone: "bad", text: err?.message || "Couldn't send the email." });
    }
  };

  // ---- subscription -------------------------------------------------------
  const doCancel = async () => {
    setSubBusy(true);
    setSubMsg(null);
    try {
      const why = reason === "Other" ? `Other: ${reasonText}` : [reason, reasonText].filter(Boolean).join(" — ");
      const res = await httpsCallable(functions, "cancelMySubscription")({ reason: why });
      setSub(res.data);
      setCancelStep(0);
      setSubMsg({ tone: "ok", text: `Your subscription is cancelled. You won't be charged again, and everything stays open until ${fmtDate(res.data?.currentPeriodEnd)}.` });
    } catch (err) {
      setSubMsg({ tone: "bad", text: err?.message || "Couldn't cancel just now. Please try again." });
    } finally {
      setSubBusy(false);
    }
  };

  const doResume = async () => {
    setSubBusy(true);
    setSubMsg(null);
    try {
      const res = await httpsCallable(functions, "resumeMySubscription")();
      setSub(res.data);
      setSubMsg({ tone: "ok", text: "Great — your subscription will carry on as normal." });
    } catch (err) {
      setSubMsg({ tone: "bad", text: err?.message || "Couldn't change that just now." });
    } finally {
      setSubBusy(false);
    }
  };

  const trialEnds = profile?.trial?.endDate?.toMillis ? profile.trial.endDate.toMillis() : profile?.trial?.endDate ? Date.parse(profile.trial.endDate) : null;

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.topRow}>
          <h1 style={s.title}>Account settings</h1>
          <button type="button" style={s.ghost} onClick={() => navigate(dashboardPathFor(profile))}>
            ← Back to my dashboard
          </button>
        </div>

        {/* 1. account */}
        <section style={s.section}>
          <h2 style={s.h2}>Your account</h2>
          {name && (
            <p style={s.row}>
              <span style={s.k}>Name</span> {name}
            </p>
          )}
          <p style={s.row}>
            <span style={s.k}>Sign-in email</span> {user.email}
          </p>
        </section>

        {/* 2. password */}
        <section style={s.section}>
          <h2 style={s.h2}>Password</h2>
          {schoolLogin ? (
            <p style={s.note}>This is a class login made by your teacher. To change its password, ask your teacher.</p>
          ) : (
            <>
              <form onSubmit={changePassword} style={s.form}>
                <label style={s.label}>
                  Current password
                  <input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} style={s.input} required />
                </label>
                <label style={s.label}>
                  New password
                  <input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} style={s.input} required />
                </label>
                <label style={s.label}>
                  New password again
                  <input type="password" autoComplete="new-password" value={pw.again} onChange={(e) => setPw({ ...pw, again: e.target.value })} style={s.input} required />
                </label>
                <button type="submit" style={s.primary} disabled={pwBusy}>
                  {pwBusy ? "Changing…" : "Change password"}
                </button>
              </form>
              <p style={s.note}>
                Forgot your current password?{" "}
                <button type="button" style={s.link} onClick={sendReset}>
                  Email me a reset link
                </button>
              </p>
            </>
          )}
          {pwMsg && <p style={pwMsg.tone === "ok" ? s.ok : s.bad}>{pwMsg.text}</p>}
        </section>

        {/* 3. subscription */}
        <section style={s.section}>
          <h2 style={s.h2}>Subscription</h2>
          {sub === null ? (
            <p style={s.note}>Loading…</p>
          ) : sub.hasSubscription ? (
            <>
              <p style={s.row}>
                <span style={s.k}>Plan</span> {sub.planKey ? PLANS[sub.planKey]?.title || `${sub.planKey.split("_")[0].replace(/^./, (c) => c.toUpperCase())} plan` : "Smart Board Learning"}
                {sub.interval ? ` · billed ${sub.interval === "year" ? "yearly" : "monthly"}` : ""}
                {sub.quantity > 1 ? ` · ${sub.quantity} seats` : ""}
              </p>
              <p style={s.row}>
                <span style={s.k}>Status</span>{" "}
                {sub.cancelAtPeriodEnd
                  ? `Cancelled — access ends ${fmtDate(sub.currentPeriodEnd)}`
                  : sub.status === "trialing"
                  ? `Free trial — first payment ${fmtDate(sub.trialEnd || sub.currentPeriodEnd)}`
                  : `Active — renews ${fmtDate(sub.currentPeriodEnd)}`}
              </p>
              {sub.cancelAtPeriodEnd && (
                <button type="button" style={s.primary} onClick={doResume} disabled={subBusy}>
                  {subBusy ? "…" : "Keep my subscription"}
                </button>
              )}
            </>
          ) : (
            <p style={s.note}>
              {sub.seatFrom === "teacher"
                ? "Your access comes from your teacher's class, so there's nothing to manage here."
                : sub.seatFrom === "school"
                ? "Your access comes from a seat your school or teacher bought, so there's nothing to manage here."
                : trialEnds
                ? `You're on a free trial until ${fmtDate(trialEnds)}. Nothing will be charged unless you choose a plan.`
                : "There's no paid subscription on this account."}
            </p>
          )}
          {subErr && <p style={s.bad}>{subErr}</p>}
          {subMsg && <p style={subMsg.tone === "ok" ? s.ok : s.bad}>{subMsg.text}</p>}

          {/* Deliberately quiet: small text at the bottom, two steps. */}
          {sub?.hasSubscription && !sub.cancelAtPeriodEnd && (
            <div style={s.cancelBox}>
              {cancelStep === 0 && (
                <button type="button" style={s.quietLink} onClick={() => setCancelStep(1)}>
                  Cancel subscription
                </button>
              )}
              {cancelStep >= 1 && (
                <div style={s.cancelPanel}>
                  <p style={s.cancelTitle}>We're sorry to see you go. Can you tell us why?</p>
                  <div style={s.reasons}>
                    {REASONS.map((r) => (
                      <label key={r} style={s.reason}>
                        <input type="radio" name="cancel-reason" checked={reason === r} onChange={() => setReason(r)} /> {r}
                      </label>
                    ))}
                  </div>
                  <textarea style={s.textarea} rows={2} placeholder="Anything else? (optional)" value={reasonText} onChange={(e) => setReasonText(e.target.value.slice(0, 250))} />
                  <p style={s.note}>
                    You won't be charged again. You keep full access until {fmtDate(sub.status === "trialing" ? sub.trialEnd || sub.currentPeriodEnd : sub.currentPeriodEnd)}, and you can change your mind any time before then.
                  </p>
                  <div style={s.btnRow}>
                    <button type="button" style={s.primary} onClick={() => { setCancelStep(0); setReason(""); setReasonText(""); }}>
                      Keep my subscription
                    </button>
                    {cancelStep === 1 ? (
                      <button type="button" style={s.dangerSoft} disabled={!reason} onClick={() => setCancelStep(2)}>
                        Continue to cancel
                      </button>
                    ) : (
                      <button type="button" style={s.danger} disabled={subBusy} onClick={doCancel}>
                        {subBusy ? "Cancelling…" : "Yes, cancel my subscription"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#f1f5f9", display: "flex", justifyContent: "center", padding: "32px 16px", boxSizing: "border-box" },
  card: { background: "#fff", width: "100%", maxWidth: 640, borderRadius: 14, padding: "26px 28px", boxShadow: "0 20px 40px rgba(0,0,0,0.10)", alignSelf: "flex-start" },
  topRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  title: { margin: 0, fontSize: "1.8rem", color: "#111" },
  section: { borderTop: "1px solid #e5e7eb", marginTop: 20, paddingTop: 16 },
  h2: { margin: "0 0 10px", fontSize: "1.15rem", color: "#1f2937" },
  row: { margin: "6px 0", color: "#374151", fontSize: 15 },
  k: { display: "inline-block", minWidth: 110, fontWeight: 700, color: "#6b7280" },
  note: { color: "#6b7280", fontSize: 14, lineHeight: 1.5, margin: "8px 0" },
  form: { display: "grid", gap: 10, maxWidth: 360 },
  label: { display: "grid", gap: 4, fontSize: 13, fontWeight: 700, color: "#374151" },
  input: { padding: "9px 11px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 15 },
  primary: { border: 0, borderRadius: 8, padding: "10px 16px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", justifySelf: "start" },
  ghost: { border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer" },
  link: { border: 0, background: "none", padding: 0, color: "#2563eb", textDecoration: "underline", cursor: "pointer", fontSize: 14 },
  ok: { color: "#15803d", fontWeight: 700, fontSize: 14 },
  bad: { color: "#b91c1c", fontWeight: 700, fontSize: 14 },
  cancelBox: { marginTop: 26 },
  quietLink: { border: 0, background: "none", padding: 0, color: "#9ca3af", textDecoration: "underline", cursor: "pointer", fontSize: 12 },
  cancelPanel: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fafafa" },
  cancelTitle: { margin: "0 0 8px", fontWeight: 700, color: "#374151" },
  reasons: { display: "grid", gap: 6, marginBottom: 8 },
  reason: { fontSize: 14, color: "#374151", display: "flex", gap: 6, alignItems: "center" },
  textarea: { width: "100%", boxSizing: "border-box", padding: 8, border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, fontFamily: "inherit" },
  btnRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 },
  dangerSoft: { border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 16px", background: "#fff", color: "#6b7280", fontWeight: 600, cursor: "pointer" },
  danger: { border: 0, borderRadius: 8, padding: "10px 16px", background: "#dc2626", color: "#fff", fontWeight: 700, cursor: "pointer" },
};
